import { DeviceType, PhysicalDeviceType } from '../../../../shared/models/device';
import { TileFormat } from '../../../../shared/models/tile';
import { ProtocolPropertyLabel } from '../../../../shared/constants/protocol-property-label.constants';

/**
 * Single source of truth for CSV column definitions
 * These match the interfaces in csv-tile.ts and glossary documentation
 */

/**
 * Enum for CSV field IDs used across bulk operations
 * Provides type safety and prevents typos in field references
 */
export enum CsvFieldId {
  // Configuration-specific fields (for bulk updates)
  TILE_ID = 'Tile ID',

  // Common tile fields
  TILE_NAME = 'Tile Name',
  DISPLAY_NAME = 'Display Name',
  TILE_WRITABILITY = 'Tile Writability',
  COLOR = 'Color',
  ICON = 'Icon',
  LINKED_APP_IDS = 'Linked App IDs',
  LINKED_SUPER_TILE_IDS = 'Linked Super Tile IDs',
  AUTO_CLEAR_ALARMS = 'Auto-Clear Alarms',

  // Numeric tile fields
  DATA_DISPLAY_UNITS = 'Data Display Units',
  DECIMAL_PLACES = 'Decimal Places',
  TRANSFORMATION_CONVERSION_FORMULA = 'Transformation Conversion Formula',
  MULTIPLIER = 'Multiplier',
  CALIBRATION_OFFSET = 'Calibration Offset',
  MINIMUM_WRITABLE_VALUE = 'Minimum Writable Value',
  MAXIMUM_WRITABLE_VALUE = 'Maximum Writable Value',

  // Toggle tile fields
  DATA_DISPLAY_TRUE_PHRASE = 'Data Display True Phrase',
  DATA_DISPLAY_FALSE_PHRASE = 'Data Display False Phrase',

  // Alarm condition fields (common prefixes)
  CRITICAL_ENABLED = 'Critical: Enabled',
  CRITICAL_OPERATOR = 'Critical: Operator',
  CRITICAL_VALUE = 'Critical: Value',
  CRITICAL_DELAY = 'Critical: Delay',
  CRITICAL_MESSAGE = 'Critical: Message',
  CRITICAL_ICON = 'Critical: Icon',

  HIGH_CRITICAL_ENABLED = 'High Critical: Enabled',
  HIGH_CRITICAL_OPERATOR = 'High Critical: Operator',
  HIGH_CRITICAL_VALUE = 'High Critical: Value',
  HIGH_CRITICAL_DELAY = 'High Critical: Delay',
  HIGH_CRITICAL_MESSAGE = 'High Critical: Message',
  HIGH_CRITICAL_ICON = 'High Critical: Icon',

  LOW_CRITICAL_ENABLED = 'Low Critical: Enabled',
  LOW_CRITICAL_OPERATOR = 'Low Critical: Operator',
  LOW_CRITICAL_VALUE = 'Low Critical: Value',
  LOW_CRITICAL_DELAY = 'Low Critical: Delay',
  LOW_CRITICAL_MESSAGE = 'Low Critical: Message',
  LOW_CRITICAL_ICON = 'Low Critical: Icon',

  HIGH_WARNING_ENABLED = 'High Warning: Enabled',
  HIGH_WARNING_OPERATOR = 'High Warning: Operator',
  HIGH_WARNING_VALUE = 'High Warning: Value',
  HIGH_WARNING_DELAY = 'High Warning: Delay',
  HIGH_WARNING_MESSAGE = 'High Warning: Message',
  HIGH_WARNING_ICON = 'High Warning: Icon',

  LOW_WARNING_ENABLED = 'Low Warning: Enabled',
  LOW_WARNING_OPERATOR = 'Low Warning: Operator',
  LOW_WARNING_VALUE = 'Low Warning: Value',
  LOW_WARNING_DELAY = 'Low Warning: Delay',
  LOW_WARNING_MESSAGE = 'Low Warning: Message',
  LOW_WARNING_ICON = 'Low Warning: Icon',

  // Protocol-specific fields
  MOTION_DEVICE_ID = 'Motion Device ID',

  // BACnet fields
  BACNET_INSTANCE_NUMBER = ProtocolPropertyLabel.BACNET_INSTANCE_NUMBER,
  BACNET_OBJECT_TYPE = ProtocolPropertyLabel.BACNET_OBJECT_TYPE,

  // Modbus fields
  MODBUS_ADDRESS = ProtocolPropertyLabel.MODBUS_ADDRESS,
  MODBUS_DATA_TYPE = ProtocolPropertyLabel.MODBUS_DATA_TYPE,
  MODBUS_BYTE_ORDER = ProtocolPropertyLabel.MODBUS_BYTE_ORDER,
  MODBUS_FUNCTIONS = ProtocolPropertyLabel.MODBUS_FUNCTIONS,

  // LoRaWAN fields
  LORAWAN_POINT_NAME = ProtocolPropertyLabel.LORAWAN_POINT_NAME,
  LORAWAN_DATA_SOURCE = ProtocolPropertyLabel.LORAWAN_DATA_SOURCE,
  LORAWAN_DATA_TYPE = ProtocolPropertyLabel.LORAWAN_DATA_TYPE,

  // Formula tile fields
  FORMULA_TYPE = 'Formula Type',
  FORMULA_VALUE = 'Formula Value',

  // Tag/facet fields
  PROFILE = 'Profile',
  TAGS = 'Tags',
  FACETS = 'Facets',
}

// BACnet-specific columns (as shown in glossary)
export const BACNET_POINT_COLUMNS: CsvFieldId[] = [
  CsvFieldId.MOTION_DEVICE_ID,         // Unique identifier for the device
  CsvFieldId.BACNET_INSTANCE_NUMBER,   // BACnet object address
  CsvFieldId.BACNET_OBJECT_TYPE        // BACnet object type (analogValue/binaryValue determines tile type)
];

// Modbus-specific columns (as shown in glossary)
export const MODBUS_POINT_COLUMNS: CsvFieldId[] = [
  CsvFieldId.MOTION_DEVICE_ID,     // Unique identifier for the device
  CsvFieldId.MODBUS_ADDRESS,       // Modbus register address
  CsvFieldId.MODBUS_DATA_TYPE,     // Modbus data type (bool for toggle, int16/int32/float32/float64 for numeric)
  CsvFieldId.MODBUS_BYTE_ORDER,    // Endianness: Aa (Big-endian), aA (Little-endian)
  CsvFieldId.MODBUS_FUNCTIONS      // Protocol-level writability (true/false)
];

// LoRaWAN-specific columns
export const LORAWAN_POINT_COLUMNS: CsvFieldId[] = [
  CsvFieldId.MOTION_DEVICE_ID,      // Unique identifier for the device
  CsvFieldId.LORAWAN_POINT_NAME,    // Name of the discovered point (e.g., "temperature", "battery")
  CsvFieldId.LORAWAN_DATA_SOURCE,   // UP (Uplink Payload) or UM (Uplink Metadata)
  CsvFieldId.LORAWAN_DATA_TYPE      // num (numeric) or bool (boolean)
];

// Common tile columns (shared between creation and configuration)
const COMMON_TILE_COLUMNS: CsvFieldId[] = [
  CsvFieldId.TILE_NAME,
  CsvFieldId.DISPLAY_NAME,
  CsvFieldId.TILE_WRITABILITY,
  CsvFieldId.COLOR,
  CsvFieldId.ICON
];

// Tile creation columns - includes app and super tile linking
// Used for bulk tile creation where new tiles are linked to apps/super tiles
export const TILE_CREATION_CONFIG_COLUMNS: CsvFieldId[] = [
  ...COMMON_TILE_COLUMNS,
  CsvFieldId.LINKED_APP_IDS,          // Links new tiles to apps
  CsvFieldId.LINKED_SUPER_TILE_IDS,   // Links new tiles to super tiles
  CsvFieldId.AUTO_CLEAR_ALARMS,
  CsvFieldId.PROFILE,                 // Optional: tag profile label (e.g. Data Center) — used for facet resolution; derived from facets on export
  CsvFieldId.TAGS,                    // Optional: pipe-separated tag labels (e.g. Critical|Legacy)
  CsvFieldId.FACETS,                  // Optional: pipe-separated KEY=Value facet pairs (e.g. BUILDING=Building|FLOOR=1)
];

// Formula tile creation columns — same as TILE_CREATION_CONFIG_COLUMNS but without
// writability (formula tiles are always read-only, per PLAT-303)
export const FORMULA_CREATION_CONFIG_COLUMNS: CsvFieldId[] = TILE_CREATION_CONFIG_COLUMNS.filter(
  col => col !== CsvFieldId.TILE_WRITABILITY
);

// Tile configuration columns - includes app IDs (fully functional)
// Used for bulk tile configuration updates
// Note: "Linked App IDs" column is fully functional during configuration:
// - Service calculates diff between current and requested apps
// - Links tiles to apps that aren't currently linked
// - Unlinks tiles from apps not in the CSV
// - Empty column or missing IDs will unlink all apps
// "Linked Super Tile IDs" is add-only during configuration: tiles are added to the listed
// folders, existing folder memberships are never removed, and an empty column is a no-op
export const TILE_CONFIGURATION_COLUMNS: CsvFieldId[] = [
  ...COMMON_TILE_COLUMNS,
  CsvFieldId.LINKED_APP_IDS,          // Functional - modifies app relationships (links/unlinks)
  CsvFieldId.LINKED_SUPER_TILE_IDS,   // Functional - adds tiles to folders, never removes
  CsvFieldId.AUTO_CLEAR_ALARMS,
  CsvFieldId.PROFILE,                 // Optional: tag profile label (e.g. Data Center) — used for facet resolution; derived from facets on export
  CsvFieldId.TAGS,                    // Optional: pipe-separated tag labels (e.g. Critical|Legacy)
  CsvFieldId.FACETS,                  // Optional: pipe-separated KEY=Value facet pairs (e.g. BUILDING=Building|FLOOR=1)
];

export const TOGGLE_SPECIFIC_COLUMNS: CsvFieldId[] = [
  CsvFieldId.DATA_DISPLAY_TRUE_PHRASE,
  CsvFieldId.DATA_DISPLAY_FALSE_PHRASE,
  CsvFieldId.CRITICAL_ENABLED,
  CsvFieldId.CRITICAL_VALUE,
  CsvFieldId.CRITICAL_DELAY,
  CsvFieldId.CRITICAL_MESSAGE,
  CsvFieldId.CRITICAL_ICON
];

export const NUMERIC_SPECIFIC_COLUMNS: CsvFieldId[] = [
  CsvFieldId.DATA_DISPLAY_UNITS,
  CsvFieldId.DECIMAL_PLACES,
  CsvFieldId.TRANSFORMATION_CONVERSION_FORMULA,
  CsvFieldId.MULTIPLIER,
  CsvFieldId.CALIBRATION_OFFSET,
  CsvFieldId.MINIMUM_WRITABLE_VALUE,
  CsvFieldId.MAXIMUM_WRITABLE_VALUE,
  CsvFieldId.HIGH_CRITICAL_ENABLED,
  CsvFieldId.HIGH_CRITICAL_OPERATOR,
  CsvFieldId.HIGH_CRITICAL_VALUE,
  CsvFieldId.HIGH_CRITICAL_DELAY,
  CsvFieldId.HIGH_CRITICAL_MESSAGE,
  CsvFieldId.HIGH_CRITICAL_ICON,
  CsvFieldId.LOW_CRITICAL_ENABLED,
  CsvFieldId.LOW_CRITICAL_OPERATOR,
  CsvFieldId.LOW_CRITICAL_VALUE,
  CsvFieldId.LOW_CRITICAL_DELAY,
  CsvFieldId.LOW_CRITICAL_MESSAGE,
  CsvFieldId.LOW_CRITICAL_ICON,
  CsvFieldId.HIGH_WARNING_ENABLED,
  CsvFieldId.HIGH_WARNING_OPERATOR,
  CsvFieldId.HIGH_WARNING_VALUE,
  CsvFieldId.HIGH_WARNING_DELAY,
  CsvFieldId.HIGH_WARNING_MESSAGE,
  CsvFieldId.HIGH_WARNING_ICON,
  CsvFieldId.LOW_WARNING_ENABLED,
  CsvFieldId.LOW_WARNING_OPERATOR,
  CsvFieldId.LOW_WARNING_VALUE,
  CsvFieldId.LOW_WARNING_DELAY,
  CsvFieldId.LOW_WARNING_MESSAGE,
  CsvFieldId.LOW_WARNING_ICON
];

/**
 * Type helper to convert column arrays into TypeScript interface
 * All columns are string | undefined for flexibility
 */
export type ColumnsToInterface<T extends readonly (string | CsvFieldId)[]> = {
  [K in T[number]]: string | undefined;
};

/**
 * Protocol-specific CSV template configuration
 * Maps protocol + tile type to columns and example values
 * Only supports Numeric and Toggle formats for CSV templates
 */
export const PROTOCOL_CSV_CONFIG: Record<PhysicalDeviceType, Partial<Record<TileFormat, { columns: CsvFieldId[], examples: any }>>> = {
  [DeviceType.BACnet]: {
    [TileFormat.Numeric]: {
      columns: [
        ...BACNET_POINT_COLUMNS,      // Motion Device ID, BACnet Instance Number, BACnet Object Type
        ...TILE_CREATION_CONFIG_COLUMNS,        // Tile Name, Display Name, etc.
        ...NUMERIC_SPECIFIC_COLUMNS    // Data Display Units, Decimal Places, etc.
      ],
      examples: {}
    },
    [TileFormat.Toggle]: {
      columns: [
        ...BACNET_POINT_COLUMNS,      // Motion Device ID, BACnet Instance Number, BACnet Object Type
        ...TILE_CREATION_CONFIG_COLUMNS,        // Tile Name, Display Name, etc.
        ...TOGGLE_SPECIFIC_COLUMNS     // Data Display True/False Phrase, Critical fields
      ],
      examples: {}
    }
  },
  [DeviceType.Modbus]: {
    [TileFormat.Numeric]: {
      columns: [
        ...MODBUS_POINT_COLUMNS,      // Motion Device ID, Point ID, Data Type, Byte Order, Point Writable
        ...TILE_CREATION_CONFIG_COLUMNS,        // Tile Name, Display Name, etc.
        ...NUMERIC_SPECIFIC_COLUMNS    // Data Display Units, Decimal Places, etc.
      ],
      examples: {}
    },
    [TileFormat.Toggle]: {
      columns: [
        ...MODBUS_POINT_COLUMNS,      // Motion Device ID, Point ID, Data Type, Byte Order, Point Writable
        ...TILE_CREATION_CONFIG_COLUMNS,        // Tile Name, Display Name, etc.
        ...TOGGLE_SPECIFIC_COLUMNS     // Data Display True/False Phrase, Critical fields
      ],
      examples: {}
    }
  },
  [DeviceType.LoRaWAN]: {
    [TileFormat.Numeric]: {
      columns: [
        ...LORAWAN_POINT_COLUMNS,      // Motion Device ID, Point Name, Point Type, Point Format
        ...TILE_CREATION_CONFIG_COLUMNS,         // Tile Name, Display Name, etc.
        ...NUMERIC_SPECIFIC_COLUMNS     // Data Display Units, Decimal Places, etc.
      ],
      examples: {}
    },
    [TileFormat.Toggle]: {
      columns: [
        ...LORAWAN_POINT_COLUMNS,      // Motion Device ID, Point Name, Point Type, Point Format
        ...TILE_CREATION_CONFIG_COLUMNS,         // Tile Name, Display Name, etc.
        ...TOGGLE_SPECIFIC_COLUMNS      // Data Display True/False Phrase, Critical fields
      ],
      examples: {}
    }
  }
} as const;

// Formula numeric columns — same as NUMERIC_SPECIFIC_COLUMNS but without writable-range
// fields (min/max writable value) since formula tiles are always read-only (PLAT-303)
export const FORMULA_NUMERIC_SPECIFIC_COLUMNS: CsvFieldId[] = NUMERIC_SPECIFIC_COLUMNS.filter(
  col => col !== CsvFieldId.MINIMUM_WRITABLE_VALUE && col !== CsvFieldId.MAXIMUM_WRITABLE_VALUE
);

/**
 * Returns all CSV columns for a single formula binding at position N (1-based).
 * Column names mirror the existing LoRaWAN and numeric column labels.
 */
export function getFormulaBindingColumns(n: number): string[] {
  return [
    `Binding ${n}: Variable`,
    `Binding ${n}: ${CsvFieldId.MOTION_DEVICE_ID}`,
    `Binding ${n}: ${CsvFieldId.LORAWAN_POINT_NAME}`,
    `Binding ${n}: ${CsvFieldId.LORAWAN_DATA_SOURCE}`,
    `Binding ${n}: ${CsvFieldId.LORAWAN_DATA_TYPE}`,
    `Binding ${n}: ${CsvFieldId.MULTIPLIER}`,
    `Binding ${n}: ${CsvFieldId.TRANSFORMATION_CONVERSION_FORMULA}`,
    `Binding ${n}: ${CsvFieldId.CALIBRATION_OFFSET}`,
  ];
}
